type ConsentEditorSelectionHint = {
  documentId: string;
  pmFrom?: number;
  pmTo?: number;
  plainTextStart?: number;
  plainTextEnd?: number;
};

const PLACEMENT_SESSION_SCHEMA_VERSION = 1;

type PlacementConsentDraftContext = {
  passage?: string;
  editorSelectionHint?: ConsentEditorSelectionHint | Record<string, unknown>;
};

type PlacementSessionSnapshot = {
  v?: number;
  status?: string;
  context?: {
    draftContext?: PlacementConsentDraftContext | null;
  };
};

function normalizeConsentEditorSelectionHint(
  hint: unknown,
): ConsentEditorSelectionHint | undefined {
  if (!hint || typeof hint !== "object") return undefined;
  const raw = hint as Record<string, unknown>;
  const documentId = typeof raw.documentId === "string" ? raw.documentId.trim() : "";
  if (!documentId) return undefined;
  return {
    documentId,
    ...(typeof raw.pmFrom === "number" ? { pmFrom: raw.pmFrom } : {}),
    ...(typeof raw.pmTo === "number" ? { pmTo: raw.pmTo } : {}),
    ...(typeof raw.plainTextStart === "number" ? { plainTextStart: raw.plainTextStart } : {}),
    ...(typeof raw.plainTextEnd === "number" ? { plainTextEnd: raw.plainTextEnd } : {}),
  };
}

function readPlacementSessionDraftReplayFromMetadata(metadata: unknown): {
  passage?: string;
  editorSelectionHint?: ConsentEditorSelectionHint;
} | null {
  if (!metadata || typeof metadata !== "object") return null;
  const raw = metadata as { placementSession?: PlacementSessionSnapshot };
  const session = raw.placementSession;
  if (!session || session.v !== PLACEMENT_SESSION_SCHEMA_VERSION) return null;
  const draftContext = session.context?.draftContext;
  if (!draftContext) return null;
  const passage = typeof draftContext.passage === "string" && draftContext.passage.trim()
    ? draftContext.passage.trim()
    : "";
  const hint = normalizeConsentEditorSelectionHint(draftContext.editorSelectionHint);
  if (!passage && !hint) return null;
  return {
    ...(passage ? { passage } : {}),
    ...(hint ? { editorSelectionHint: hint } : {}),
  };
}

export function readPendingManuscriptDraftReplayFromAssistantMetadata(metadata: unknown): {
  passage?: string;
  editorSelectionHint?: ConsentEditorSelectionHint;
} | null {
  const fromSession = readPlacementSessionDraftReplayFromMetadata(metadata);
  if (fromSession?.passage || fromSession?.editorSelectionHint) return fromSession;

  if (!metadata || typeof metadata !== "object") return null;
  const raw = metadata as {
    pendingManuscriptDraft?: {
      passage?: unknown;
      editorSelectionHint?: unknown;
    };
    rewriteTargetText?: unknown;
    passage?: unknown;
    editorSelectionHint?: unknown;
  };
  const draft = raw.pendingManuscriptDraft;
  const passage =
    (typeof draft?.passage === "string" && draft.passage.trim() ? draft.passage.trim() : "")
    || (typeof raw.rewriteTargetText === "string" && raw.rewriteTargetText.trim()
      ? raw.rewriteTargetText.trim()
      : "")
    || (typeof raw.passage === "string" && raw.passage.trim() ? raw.passage.trim() : "");
  const hint =
    normalizeConsentEditorSelectionHint(draft?.editorSelectionHint)
    ?? normalizeConsentEditorSelectionHint(raw.editorSelectionHint);
  if (!passage && !hint) return null;
  return {
    ...(passage ? { passage } : {}),
    ...(hint ? { editorSelectionHint: hint } : {}),
  };
}

export function assistantMetadataHasAwaitingPlacementConsent(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== "object") return false;
  const raw = metadata as { placementSession?: PlacementSessionSnapshot };
  const session = raw.placementSession;
  if (session?.v === PLACEMENT_SESSION_SCHEMA_VERSION) {
    return session.status === "awaiting_consent";
  }
  const draft = (metadata as { pendingManuscriptDraft?: { status?: string } }).pendingManuscriptDraft;
  return draft?.status === "awaiting_insert_consent" || draft?.status === "insert_failed";
}
